import React, { useState } from 'react';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Paper,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  EmailOutlined,
  PersonOutlined,
  AssignmentIndOutlined,
} from '@mui/icons-material';
import { httpClient } from '../axios';
import { useNavigate } from 'react-router-dom';

const EnhancedAttractiveRegister = () => {
  const [user, setUser] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let errorMsg = '';

    if (!value) {
      errorMsg = `${
        name.charAt(0).toUpperCase() + name.slice(1)
      } is required`;
    } else if (name === 'email' && !/\S+@\S+\.\S+/.test(value)) {
      errorMsg = 'Enter a valid email';
    } else if (name === 'password' && value.length < 6) {
      errorMsg = 'Password must be at least 6 characters';
    }

    setErrors({ ...errors, [name]: errorMsg });
  };

  const handleRegister = async () => {
    if (
      Object.values(errors).some((error) => error) ||
      Object.values(user).some((value) => !value)
    ) {
      setError('Please fill all fields correctly!');
      return;
    }

    try {
      const response = await httpClient.post('/auth/register', user);
      console.log('Registration successful:', response.data);
      navigate('/login', {
        state: { registrationSuccess: true },
      });
    } catch (err) {
      console.error(
        'Registration Error:',
        err.response?.data || err.message
      );
      setError(err.response?.data?.message || 'Registration failed!');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background:
          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          width: '120%',
          height: '120%',
          background:
            'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          transform: 'rotate(45deg)',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          right: '-10%',
          width: '120%',
          height: '120%',
          background:
            'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          transform: 'rotate(-45deg)',
          zIndex: 1,
        }}
      />

      <Container
        maxWidth='sm'
        sx={{ position: 'relative', zIndex: 2 }}
      >
        <Paper
          elevation={24}
          sx={{
            borderRadius: 5,
            padding: 4,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 30px 60px rgba(0,0,0,0.2)',
            border: '1px solid rgba(255,255,255,0.3)',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-10px)',
              boxShadow: '0 40px 70px rgba(0,0,0,0.25)',
            },
          }}
        >
          {/* Animated Gradient Border */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 5,
              background: 'linear-gradient(90deg, #667eea, #764ba2)',
              backgroundSize: '200% 100%',
              animation: 'gradient-flow 3s ease infinite',
              '@keyframes gradient-flow': {
                '0%': { backgroundPosition: '100% 0' },
                '100%': { backgroundPosition: '-100% 0' },
              },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                background:
                  'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
                boxShadow: '0 15px 30px rgba(102,126,234,0.4)',
                transform: 'perspective(500px) rotateX(10deg)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform:
                    'perspective(500px) rotateX(0deg) scale(1.05)',
                },
              }}
            >
              <AssignmentIndOutlined
                sx={{ color: 'white', fontSize: 60 }}
              />
            </Box>
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
                background:
                  'linear-gradient(45deg, #667eea, #764ba2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Create Account
            </Typography>
            {error && (
              <Typography
                color='error'
                sx={{
                  textAlign: 'center',
                  mb: 2,
                  fontWeight: 500,
                }}
              >
                {error}
              </Typography>
            )}
          </Box>

          <TextField
            fullWidth
            label='Full Name'
            name='name'
            variant='outlined'
            margin='normal'
            value={user.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.name}
            helperText={errors.name}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <PersonOutlined color='action' />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label='Email Address'
            name='email'
            type='email'
            variant='outlined'
            margin='normal'
            value={user.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.email}
            helperText={errors.email}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <EmailOutlined color='action' />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label='Password'
            name='password'
            type={showPassword ? 'text' : 'password'}
            variant='outlined'
            margin='normal'
            value={user.password}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.password}
            helperText={errors.password}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#764ba2',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <LockOutlined color='action' />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge='end'
                  >
                    {showPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            fullWidth
            label='Role'
            name='role'
            variant='outlined'
            margin='normal'
            value={user.role}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.role}
            helperText={errors.role}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#764ba2',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <AssignmentIndOutlined color='action' />
                </InputAdornment>
              ),
            }}
          >
            {/* <MenuItem value="ADMIN">Admin</MenuItem> */}
            <MenuItem value='EMPLOYEE'>Employee</MenuItem>
          </TextField>

          <Button
            variant='contained'
            fullWidth
            onClick={handleRegister}
            sx={{
              mt: 2,
              py: 1.5,
              background:
                'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
              transition: 'all 0.3s ease',
              fontWeight: 600,
              letterSpacing: 1,
              '&:hover': {
                transform: 'scale(1.03)',
                boxShadow: '0 15px 30px rgba(102,126,234,0.4)',
              },
            }}
          >
            Register
          </Button>

          <Typography
            variant='body2'
            sx={{
              mt: 2,
              textAlign: 'center',
              color: '#7f8c8d',
            }}
          >
            Already have an account?
            <Button
              color='primary'
              size='small'
              sx={{
                ml: 1,
                color: '#667eea',
                fontWeight: 600,
                '&:hover': {
                  background: 'rgba(102,126,234,0.1)',
                },
              }}
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default EnhancedAttractiveRegister;
